import { useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { AudioPlayer } from 'src/meetings/detail/tabs/transcript/AudioPlayer';
import { ContentPaneOverlay } from 'src/UI/app-container/ContentPaneOverlay';
import { GenericErrorPage } from '../../error/GenericErrorPage';
import PageLoader from '../../UI/loader/PageLoader';
import { EntityWithFile, UploadedFile } from '../hooks/file-uploads/types';
import { useFileUploads } from '../hooks/file-uploads/useFileUploads';
import { useFileTemporaryLinkMutation } from '../hooks/useFileTemporaryLinkMutation';
import { useDocumentAudioPlayer } from '../hooks/useDocumentAudioPlayer';
import { isAudioFile } from './allowedFileTypes';
import FileUploadLayout from './FileUploadLayout';
import FiledDocumentInlineActions from './components/FiledDocumentInlineActions';
import FiledDocumentsHeaderActions from './components/FiledDocumentsHeaderActions';
import SelectionHeaderActions from './components/SelectionHeaderActions';
import { useFiledDocumentsView } from './hooks/useFiledDocumentsView';
import { useBulkDocumentSelection } from './hooks/useBulkDocumentSelection';
import BulkDeleteConfirmModal from './components/BulkDeleteConfirmModal';
import { downloadViaAnchor } from './utils/downloadViaAnchor';
import FilesTable from './files-table/FilesTable';
import { filedDocumentColumns } from './files-table/filedDocumentColumns';
import { buildFiledDocumentRowActions } from './files-table/filedDocumentRowActions';
import { CreateFolderModal } from './modals/CreateFolderModal';
import { RenameFolderModal } from './modals/RenameFolderModal';
import MoveFileModal from './files-table/MoveFileModal';

export default function FilesPage() {
  const { sectionSlug, folderId } = useParams<{ sectionSlug?: string; folderId?: string }>();

  const currentFolder =
    folderId || (sectionSlug?.startsWith('folder/') ? sectionSlug.replace('folder/', '') : null);
  const navigate = useNavigate();
  const { clientId } = useCurrentClient();
  const [createFolderOpened, { open: openCreateFolder, close: closeCreateFolder }] =
    useDisclosure(false);
  const [selectedFileForMove, setSelectedFileForMove] = useState<UploadedFile | null>(null);
  const [moveModalOpened, { open: openMoveModal, close: closeMoveModal }] = useDisclosure(false);
  const [selectedFolderForRename, setSelectedFolderForRename] = useState<UploadedFile | null>(null);
  const [renameModalOpened, { open: openRenameModal, close: closeRenameModal }] =
    useDisclosure(false);
  const [fileToDelete, setFileToDelete] = useState<UploadedFile | null>(null);

  const {
    files,
    allOtherFiles,
    deleteFile,
    deleteError,
    resetDeleteError,
    isDeleting,
    createFolder,
    moveFile,
    renameFolder,
    isLoading: isLoadingFiles,
    error,
    refetchFiles,
  } = useFileUploads({
    autoRefreshProcessing: true,
  });
  const isLoading = isLoadingFiles;

  useEffect(() => {
    refetchFiles();
  }, [sectionSlug, folderId]);

  const {
    searchValue,
    setSearchValue,
    searchOpen,
    setSearchOpen,
    filteredFiles,
    records,
    containingFolderName,
    currentFolderName,
    parentFolderId,
  } = useFiledDocumentsView({
    files,
    allOtherFiles,
    currentFolder,
    routeKey: `${sectionSlug ?? ''}/${folderId ?? ''}`,
  });

  const { createLink } = useFileTemporaryLinkMutation();

  const {
    selection,
    selectedRecords,
    bulkDeleteNames,
    requestBulkDelete,
    cancelBulkDelete,
    confirmBulkDelete,
    isBulkDeleting,
    isBulkDownloading,
    downloadSelected,
  } = useBulkDocumentSelection({
    records,
    entityName: 'file',
    nameOf: record => record.file.fileName,
    deleteRecord: record => deleteFile(record.id),
    downloadRecord: record => handleDownloadFile(record),
    refetch: refetchFiles,
    logContext: { clientId },
  });

  const handleDeletePress = (file: UploadedFile) => {
    resetDeleteError();
    setFileToDelete(file);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await deleteFile(fileToDelete.id);
    } catch (e) {
      notifyAndLogError(
        'Failed to delete file',
        'An error occurred while trying to delete the file. Please try again later.',
        e,
        { clientId, fileId: fileToDelete.id },
      );
    }
    setFileToDelete(null);
  };

  const audioPlayer = useDocumentAudioPlayer();

  const handleDownloadFile = async (uploadedFile: UploadedFile) => {
    try {
      await downloadViaAnchor(createLink, uploadedFile.file.id, uploadedFile.file.fileName);
    } catch (error) {
      notifyAndLogError(
        'Failed to download file',
        'An error occurred while trying to download the file. Please try again later.',
        error,
        { clientId, fileId: uploadedFile.file.id },
      );
    }
  };

  const handleDownloadPress = (file: UploadedFile) => {
    if (isAudioFile(file.file.fileType, file.file.fileName)) {
      audioPlayer.download(file, clientId);
      return;
    }
    handleDownloadFile(file);
  };

  const handleFolderClick = (folder: EntityWithFile) => {
    navigate(`/client/${clientId}/document/files/folder/${folder.id}`);
  };

  const handleFileDocument = (file: UploadedFile) => {
    setSelectedFileForMove(file);
    openMoveModal();
  };

  const handleMoveFile = async (file: UploadedFile, parentId: string | null | undefined) => {
    try {
      await moveFile(file, parentId);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to move file',
        'An error occurred while trying to move the file. Please try again later.',
        error,
        { clientId, fileId: file.id, parentId },
      );
    }
  };

  const handleBackFromFolder = () => {
    navigate(
      parentFolderId
        ? `/client/${clientId}/document/files/folder/${parentFolderId}`
        : `/client/${clientId}/document/files`,
    );
  };

  const handleCreateFolder = async (folderName: string) => {
    try {
      await createFolder(folderName, currentFolder ?? undefined);
      await refetchFiles();
    } catch (error) {
      notifyAndLogError(
        'Failed to create folder',
        'An error occurred while trying to create the folder. Please try again later.',
        error,
        { clientId, folderName, currentFolder },
      );
      throw error;
    }
  };

  const handleRenameFolderClick = (folder: UploadedFile) => {
    setSelectedFolderForRename(folder);
    openRenameModal();
  };

  const handleRenameFolder = async (folder: UploadedFile, newName: string) => {
    await renameFolder(folder, newName);
    await refetchFiles();
  };

  const handleUploadClick = () => {
    navigate(`/client/${clientId}/document/add`);
  };

  if (error) {
    return <GenericErrorPage error={error} />;
  }

  if (isLoading) {
    return <PageLoader absolute={false} />;
  }

  return (
    <FileUploadLayout
      title={selection.isSelecting ? 'Select the files' : currentFolderName}
      variant="compact"
      collapsibleSearch={!selection.isSelecting}
      searchOpen={searchOpen}
      onSearchOpenChange={setSearchOpen}
      onBack={currentFolder && !selection.isSelecting ? handleBackFromFolder : undefined}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      searchResultCount={filteredFiles.length}
      searchPlaceholder="Search documents"
      actions={
        selection.isSelecting ? (
          <SelectionHeaderActions
            selectedCount={selectedRecords.length}
            onDeleteSelected={requestBulkDelete}
            onDownloadSelected={downloadSelected}
            onCancel={selection.exit}
            isDownloading={isBulkDownloading}
          />
        ) : (
          <FiledDocumentsHeaderActions
            onCreateFolder={openCreateFolder}
            onUpload={handleUploadClick}
          />
        )
      }
    >
      <FilesTable<UploadedFile>
        entitiesWithFile={records}
        isLoading={isLoading}
        additionalColumns={filedDocumentColumns}
        nameSubtitle={containingFolderName}
        inlineActions={file => (
          <FiledDocumentInlineActions file={file} onDownload={handleDownloadPress} />
        )}
        onClick={file => {
          if (file.isFolder && !selection.isSelecting) {
            handleFolderClick(file);
          }
        }}
        enableDragDrop
        actions={file =>
          buildFiledDocumentRowActions(file, {
            onPlay: f => audioPlayer.play(f.file.id, f.file.fileName),
            onRenameFolder: handleRenameFolderClick,
            onMove: handleFileDocument,
            onDelete: handleDeletePress,
            onSelect: f => selection.enter(f.id),
          })
        }
        variant="compact"
        nameColumnTitle="File name"
        selection={
          selection.isSelecting
            ? {
                selectedIds: selection.selectedIds,
                onToggle: selection.toggle,
                isSelectable: record => !record.isFolder,
              }
            : undefined
        }
      />

      <CreateFolderModal
        opened={createFolderOpened}
        onClose={closeCreateFolder}
        onCreateFolder={handleCreateFolder}
      />

      {selectedFileForMove && (
        <MoveFileModal
          opened={moveModalOpened}
          close={closeMoveModal}
          file={selectedFileForMove}
          folders={files}
          rootLabel="Filed documents"
          onFileMoved={handleMoveFile}
        />
      )}

      {selectedFolderForRename && (
        <RenameFolderModal
          opened={renameModalOpened}
          onClose={closeRenameModal}
          folder={selectedFolderForRename}
          onRename={handleRenameFolder}
        />
      )}

      <ConfirmDeleteModal
        opened={!!fileToDelete}
        onClose={() => setFileToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title={fileToDelete?.isFolder ? 'Delete folder' : 'Delete file'}
        message={`Are you sure you want to delete "${fileToDelete?.file.fileName}"? This action cannot be undone.`}
        error={deleteError}
        isLoading={isDeleting}
      />

      <BulkDeleteConfirmModal
        fileNames={bulkDeleteNames}
        entityName="file"
        onClose={cancelBulkDelete}
        onConfirm={confirmBulkDelete}
        isLoading={isBulkDeleting}
      />

      {audioPlayer.isVisible && (
        <ContentPaneOverlay>
          <AudioPlayer
            isPlaying={audioPlayer.isPlaying}
            currentTime={audioPlayer.currentTime}
            duration={audioPlayer.duration}
            isMuted={audioPlayer.isMuted}
            onPlayPause={audioPlayer.handlePlayPause}
            onSeek={audioPlayer.handleSeek}
            onMuteToggle={audioPlayer.handleMuteToggle}
            fileName={audioPlayer.fileName}
            onClose={audioPlayer.handleClose}
          />
        </ContentPaneOverlay>
      )}
    </FileUploadLayout>
  );
}
